import { QuestionnaireTemplate } from '@/types';
import { formatUkDate } from './dates';

// Pure helpers for the staff "Medical Data Review" panel: turning a
// questionnaire submission's raw answers into labelled, template-ordered
// entries for auditing.

export type ReviewAnswerEntry = { key: string; label: string; value: unknown };

const QUESTION_DETAIL_SUFFIXES = new Set([
  'details',
  'detail',
  'comment',
  'comments',
  'commenttext',
  'detailstext',
  'note',
  'notes',
  'additionalinfo',
  'additionaldetails'
]);
const QUESTION_DETAIL_SUFFIX_ORDER = [
  'details',
  'detail',
  'comment',
  'comments',
  'commenttext',
  'detailstext',
  'note',
  'notes',
  'additionalinfo',
  'additionaldetails'
];

export function toTitleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatReviewFieldLabel(rawKey: string): string {
  return (
    toTitleCase(rawKey.replace(/_details$/i, '').replace(/([a-z])([A-Z])/g, '$1 $2')) +
    (/_details$/i.test(rawKey) ? ' (Details)' : '')
  );
}

export function formatReviewFieldValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return formatUkDate(text, text);
  return text || '—';
}

function hasReadableValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  return true;
}

function getQuestionScopedSuffix(key: string, questionId: string): string | null {
  const normKey = String(key || '')
    .trim()
    .toLowerCase();
  const normId = String(questionId || '')
    .trim()
    .toLowerCase();
  if (!normKey || !normId || !normKey.startsWith(normId)) return null;
  let suffix = normKey.slice(normId.length);
  if (!suffix) return '';
  if (suffix[0] === '_' || suffix[0] === '.' || suffix[0] === '-') suffix = suffix.slice(1);
  return suffix || null;
}

export function buildReviewAnswerEntries(
  rawAnswers: Record<string, unknown> | null | undefined,
  template: Pick<QuestionnaireTemplate, 'questions'> | null | undefined
): ReviewAnswerEntry[] {
  const answers = rawAnswers && typeof rawAnswers === 'object' ? rawAnswers : {};
  const raw = Object.entries(answers).filter(([, v]) => hasReadableValue(v));
  if (!raw.length) return [];
  if (!template?.questions?.length) {
    return raw.map(([key, value]) => ({ key, label: formatReviewFieldLabel(key), value }));
  }

  const consumed = new Set<string>();
  const ordered: ReviewAnswerEntry[] = [];

  template.questions.forEach((question) => {
    const entry = raw.find(
      ([key]) =>
        String(key || '')
          .trim()
          .toLowerCase() ===
        String(question.id || '')
          .trim()
          .toLowerCase()
    );
    if (entry) {
      ordered.push({
        key: entry[0],
        label: question.label || formatReviewFieldLabel(question.id),
        value: entry[1]
      });
      consumed.add(entry[0]);
    }

    const detailEntries = raw
      .map(([key, value], index) => ({
        key,
        value,
        index,
        suffix: getQuestionScopedSuffix(key, question.id)
      }))
      .filter(
        (e) => !consumed.has(e.key) && Boolean(e.suffix && QUESTION_DETAIL_SUFFIXES.has(e.suffix))
      )
      .sort((a, b) => {
        const ai = QUESTION_DETAIL_SUFFIX_ORDER.indexOf(String(a.suffix || ''));
        const bi = QUESTION_DETAIL_SUFFIX_ORDER.indexOf(String(b.suffix || ''));
        if (ai === bi) return a.index - b.index;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });

    detailEntries.forEach((de) => {
      ordered.push({
        key: de.key,
        label: `${question.detailsLabel || question.label || formatReviewFieldLabel(question.id)} (Details)`,
        value: de.value
      });
      consumed.add(de.key);
    });
  });

  const remaining = raw
    .filter(([key]) => !consumed.has(key))
    .map(([key, value]) => ({
      key,
      label: formatReviewFieldLabel(key),
      value
    }));

  return [...ordered, ...remaining];
}
